import { useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Calendar, CheckCircle2, MessageCircle } from "lucide-react";
import { allLanding, ilceListesi } from "../data/ilceler";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.5, ease: "easeOut" },
  }),
};

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function injectJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export default function IlcePsikolog() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const data = allLanding[slug];

  useEffect(() => {
    if (!data) return;

    const url = `https://aysunseremet.com/${data.slug}`;

    // Meta tags
    document.title = data.title;
    setMeta("description", data.description);
    setMeta("keywords", data.keywords);
    setMeta("og:title", data.title, "property");
    setMeta("og:description", data.description, "property");
    setMeta("og:url", url, "property");
    setMeta("twitter:title", data.title);
    setMeta("twitter:description", data.description);
    setCanonical(url);

    // LocalBusiness schema (ilçe özelinde)
    injectJsonLd("ilce-localbusiness", {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "MedicalBusiness", "Psychologist"],
      "@id": `${url}#localbusiness`,
      name: `Uzman Klinik Psikolog Aysun Şeremet - ${data.ilce}`,
      url,
      telephone: "+905372153407",
      priceRange: "$$",
      image: "https://aysunseremet.com/og-image.jpg",
      address: {
        "@type": "PostalAddress",
        addressCountry: "TR",
        addressRegion: "Antalya",
        addressLocality: data.ilce,
      },
      geo: { "@type": "GeoCoordinates", latitude: data.lat, longitude: data.lng },
      areaServed: data.areaServed.map((a) => ({ "@type": "AdministrativeArea", name: a })),
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "19:00",
        },
        { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "10:00", closes: "16:00" },
      ],
      sameAs: [
        "https://www.instagram.com/uzm.klnk.psk.aysunseremet/",
        "https://wa.me/905372153407",
      ],
    });

    // Breadcrumb
    injectJsonLd("ilce-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Anasayfa", item: "https://aysunseremet.com/" },
        { "@type": "ListItem", position: 2, name: data.h1, item: url },
      ],
    });

    return () => {
      document.getElementById("ilce-localbusiness")?.remove();
      document.getElementById("ilce-breadcrumb")?.remove();
    };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <main className="relative isolate mx-auto max-w-4xl px-6 py-16 sm:py-24">
      {/* Hero */}
      <motion.header
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="mb-12 text-center"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-pink-50 px-4 py-1.5 text-sm font-medium text-pink-700 ring-1 ring-pink-200">
          <MapPin className="h-4 w-4" />
          {data.ilce}
        </div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          {data.h1}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600">
          {data.heroIntro}
        </p>

        {/* CTA'lar */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/randevu-al"
            className="inline-flex items-center gap-2 rounded-full bg-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-pink-600"
          >
            <Calendar className="h-4 w-4" />
            Randevu Al
          </Link>
          <a
            href="tel:+905372153407"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 hover:ring-gray-300"
          >
            <Phone className="h-4 w-4" />
            0537 215 34 07
          </a>
          <a
            href="https://wa.me/905372153407"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-600"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>
      </motion.header>

      {/* Hizmet alanı rozetleri */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={1}
        className="mb-16 flex flex-wrap items-center justify-center gap-2"
      >
        <span className="text-sm font-medium text-gray-500">Hizmet Alanı:</span>
        {data.areaServed.map((a) => (
          <span
            key={a}
            className="rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-700 ring-1 ring-gray-200"
          >
            {a}
          </span>
        ))}
      </motion.div>

      {/* Bölümler */}
      <div className="space-y-14">
        {data.sections.map((s, i) => (
          <motion.section
            key={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            custom={i + 2}
          >
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">{s.title}</h2>
            <p className="mt-4 text-base leading-7 text-gray-700">{s.body}</p>
          </motion.section>
        ))}
      </div>

      {/* Çalışma alanları kutusu */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        custom={3}
        className="mt-16 rounded-3xl bg-gradient-to-br from-pink-50 to-white p-8 ring-1 ring-pink-100"
      >
        <h3 className="text-xl font-bold text-gray-900">Çalıştığım Ekoller</h3>
        <ul className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            { title: "EMDR", desc: "Travma işleme ve duygu düzenleme" },
            { title: "BDT", desc: "Bilişsel Davranışçı Terapi" },
            { title: "Şema Terapi", desc: "Erken dönem örüntüler" },
          ].map((e) => (
            <li
              key={e.title}
              className="rounded-2xl bg-white p-5 ring-1 ring-gray-100"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-pink-500" />
                <span className="font-semibold text-gray-900">{e.title}</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">{e.desc}</p>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Diğer ilçeler */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        custom={4}
        className="mt-16"
      >
        <h3 className="text-xl font-bold text-gray-900">Diğer Bölgeler</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {ilceListesi
            .filter((i) => i.slug !== data.slug)
            .map((i) => (
              <Link
                key={i.slug}
                to={`/${i.slug}`}
                className="rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:ring-pink-300 hover:text-pink-700"
              >
                {i.ilce}
              </Link>
            ))}
        </div>
      </motion.div>

      {/* Alt CTA */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        custom={5}
        className="mt-20 rounded-3xl bg-pink-500 p-10 text-center text-white"
      >
        <h3 className="text-2xl font-bold sm:text-3xl">
          {data.ilce}'da Profesyonel Destek Almaya Hazır mısınız?
        </h3>
        <p className="mx-auto mt-3 max-w-xl text-pink-50">
          İlk görüşme için bize ulaşın. İhtiyacınıza uygun süreci birlikte planlayalım.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/randevu-al"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-pink-700 hover:bg-pink-50"
          >
            <Calendar className="h-4 w-4" />
            Randevu Al
          </Link>
          <a
            href="tel:+905372153407"
            className="inline-flex items-center gap-2 rounded-full bg-pink-600 px-6 py-3 text-sm font-semibold text-white hover:bg-pink-700"
          >
            <Phone className="h-4 w-4" />
            Hemen Ara
          </a>
        </div>
      </motion.div>
    </main>
  );
}
